import React from "react";

import { useTranslation } from "react-i18next";

import { ButtonGroup, Select, TextInput } from "@caelor/ui";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { useCardsStore } from "../../../../../../stores/use-cards-store";
import {
  BOOLEAN_OPTIONS,
  CORNERS_OPTIONS,
  PREFIX_OPTIONS,
  STEPS_FORMAT_OPTIONS,
} from "./options";
import {
  CARD_DESIGN,
  CARDS_GLOBAL_COLOR,
  CARDS_PROP,
  CARDS_SINGLE_COLOR,
  DESIGNS_OPTIONS,
  HIDE_COLOR_TILES,
  STANDARD_BACKGROUND_COLORS,
  STEPS_FORMAT_NUMERIC_TYPES,
  STEPS_FORMAT_START_WITH,
} from "./defaults";

const CardsOptions = () => {
  const { t } = useTranslation();
  const { data, updateCardsConfig, updateCardsData } = useCardsStore(
    (state) => ({
      data: state.data,
      updateCardsConfig: state.updateCardsConfig,
      updateCardsData: state.updateCardsData,
    })
  );

  const { card, config } = data[0] || { config: { layout: [] } };

  const hasIconInEmojiType = data.some(
    (item) => item.config.type === "emoji" && typeof item.card.icon === "string"
  );

  const hasLabelColor =
    config.layout.includes(CARDS_PROP.LABEL) ||
    DESIGNS_OPTIONS.LABEL_COLOR.includes(config.design);

  const hideColorTiles = HIDE_COLOR_TILES[config.type]?.(config) || false;

  const hasGlobalColor = CARDS_GLOBAL_COLOR.includes(config.type);

  const showColorPicker = hasLabelColor || hasGlobalColor || hasIconInEmojiType;

  const showBackgroundPicker = config.type === "emoji";

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>

        {DESIGNS_OPTIONS.PREFIX.includes(config.design) && (
          <>
            <OptionContainer>
              <OptionLabel>{t("prefix")}</OptionLabel>
              <OptionContent>
                <Select
                  options={PREFIX_OPTIONS}
                  value={
                    PREFIX_OPTIONS.find(
                      (prefix) => prefix.value === config[CARDS_PROP.PREFIX]
                    ) || undefined
                  }
                  placeholder="Select prefix"
                  onChange={({ value }) => {
                    const isCustom = value === "custom";

                    updateCardsConfig({
                      key: CARDS_PROP.PREFIX,
                      value,
                    });

                    updateCardsConfig({
                      key: CARDS_PROP.CUSTOM_PREFIX,
                      value: isCustom,
                    });
                  }}
                />
              </OptionContent>
            </OptionContainer>
            {config[CARDS_PROP.CUSTOM_PREFIX] && (
              <OptionContainer>
                <OptionLabel />
                <OptionContent>
                  <TextInput
                    value={config[CARDS_PROP.PREFIX]}
                    onChange={(value) =>
                      updateCardsConfig({
                        key: CARDS_PROP.PREFIX,
                        value,
                      })
                    }
                  />
                </OptionContent>
              </OptionContainer>
            )}
          </>
        )}

        {DESIGNS_OPTIONS.FORMAT.includes(config.design) && (
          <>
            <OptionContainer>
              <OptionLabel>{t("format")}</OptionLabel>
              <OptionContent>
                <Select
                  options={STEPS_FORMAT_OPTIONS}
                  value={
                    STEPS_FORMAT_OPTIONS.find(
                      (option) => option.value === config[CARDS_PROP.FORMAT]
                    ) || STEPS_FORMAT_OPTIONS[0]
                  }
                  onChange={({ value }) => {
                    updateCardsConfig({
                      key: CARDS_PROP.FORMAT,
                      value,
                    });
                    updateCardsConfig({
                      key: CARDS_PROP.START_WITH,
                      value: STEPS_FORMAT_START_WITH[value],
                    });
                  }}
                />
              </OptionContent>
            </OptionContainer>
            <OptionContainer>
              <OptionLabel>{t("start-with")}</OptionLabel>
              <OptionContent>
                <TextInput
                  value={config[CARDS_PROP.START_WITH]}
                  onChange={(value) => {
                    const isNumeric = STEPS_FORMAT_NUMERIC_TYPES.includes(
                      config[CARDS_PROP.FORMAT]
                    );

                    if (value === "") {
                      updateCardsConfig({
                        key: CARDS_PROP.START_WITH,
                        value: "",
                      });
                      return;
                    }

                    const filtered = isNumeric
                      ? value.replace(/[^0-9]/g, "")
                      : value.replace(/[^a-zA-Z]/gi, "");

                    const limited = filtered.slice(
                      0,
                      isNumeric &&
                        config[CARDS_PROP.DESIGN] !== CARD_DESIGN.STEPS_THREE
                        ? 4
                        : 2
                    );

                    updateCardsConfig({
                      key: CARDS_PROP.START_WITH,
                      value: limited || (isNumeric ? "01" : "A"),
                    });
                  }}
                />
              </OptionContent>
            </OptionContainer>
          </>
        )}

        {DESIGNS_OPTIONS.ICON.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("icon")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.ICON)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.ICON];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.ICON
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.SUBTEXT.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("subtext")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.TEXT)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.TEXT];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.TEXT
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.LABEL.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("label")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.LABEL)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.LABEL];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.LABEL
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.ARROW.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("arrow")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.ARROW)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.ARROW];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.ARROW
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}

        {DESIGNS_OPTIONS.LINK_TEXT.includes(config.design) && (
          <OptionContainer>
            <OptionLabel>{t("link-text")}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                i18n={t}
                items={BOOLEAN_OPTIONS}
                value={config[CARDS_PROP.LAYOUT].includes(CARDS_PROP.LINK_TEXT)}
                onClick={(isOn) => {
                  if (isOn) {
                    const layout = [...config.layout, CARDS_PROP.LINK_TEXT];
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  } else {
                    const layout = config.layout.filter(
                      (item) => item !== CARDS_PROP.LINK_TEXT
                    );
                    updateCardsConfig({
                      key: CARDS_PROP.LAYOUT,
                      value: layout,
                    });
                  }
                }}
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              i18n={t}
              items={CORNERS_OPTIONS}
              value={config[CARDS_PROP.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                updateCardsConfig({ key: CARDS_PROP.BORDER_RADIUS, value })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4)
                  updateCardsConfig({ key: CARDS_PROP.BORDER_RADIUS, value });
              }}
            />
          </OptionContent>
        </OptionContainer>

        {showBackgroundPicker && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("background")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                value={
                  card[CARDS_PROP.BACKGROUND_COLORS] ||
                  STANDARD_BACKGROUND_COLORS
                }
                onChange={(value) =>
                  updateCardsData({ key: CARDS_PROP.BACKGROUND_COLORS, value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}

        {showColorPicker && (
          <OptionContainer>
            <OptionLabel alignToTop>{t("colors")}</OptionLabel>
            <OptionContent>
              <ColorPicker
                hideColorTiles={
                  CARDS_SINGLE_COLOR.includes(config[CARDS_PROP.DESIGN]) ||
                  hideColorTiles
                }
                value={card[CARDS_PROP.COLORS]}
                onChange={(value) =>
                  updateCardsData({ key: CARDS_PROP.COLORS, value })
                }
              />
            </OptionContent>
          </OptionContainer>
        )}
      </SettingsOptionContainer>
    </>
  );
};

export default CardsOptions;
